import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { CssBaseline } from "@mui/material";
import { ThemeContextProvider } from "./contexts/ThemeContext";
import { ToastProvider } from "./components/common/ToastProvider";
import store, { persistor } from "./redux/store";

import NotFound from "./pages/NotFound";
import ComingSoon from "./pages/ComingSoon";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import PrivateRoute from "./components/PrivateRoute";
import AxiosInterceptorSetup from "./utils/AxiosInterceptorSetup";

import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import AccountMaster from "./pages/masters/AccountMaster";
import LoanTypeList from "./pages/masters/LoanTypeList";
import NoticeMaster from "./pages/masters/NoticeMaster";
import TermDepositMaster from "./pages/masters/TermDepositMaster";
import MemberPage from "./pages/masters/MemberPage";
import DepositAccounts from "./pages/loandeposits/DepositAccounts";
import IssueLoan from "./pages/loandeposits/IssueLoan";

function AppRoutes() {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  return (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />
      {/* Masters submenu routes */}
      <Route
        path="/masters/member"
        element={
          <PrivateRoute>
            <MemberPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/masters/account"
        element={
          <PrivateRoute>
            <AccountMaster />
          </PrivateRoute>
        }
      />

      <Route
        path="/masters/deposit"
        element={
          <PrivateRoute>
            <TermDepositMaster />
          </PrivateRoute>
        }
      />
      <Route
        path="/masters/loan"
        element={
          <PrivateRoute>
            <LoanTypeList />
          </PrivateRoute>
        }
      />
      <Route
        path="/masters/notice"
        element={
          <PrivateRoute>
            <NoticeMaster />
          </PrivateRoute>
        }
      />
      <Route
        path="/masters/option"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Option Master"
              description="Configure system options, parameters, and default settings. Options management panel coming soon."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/masters/user"
        element={
          <PrivateRoute>
            <ComingSoon
              title="User Master"
              description="Manage user accounts, roles, permissions, and access controls. User management system under development."
            />
          </PrivateRoute>
        }
      />

      {/* Loan & Deposits submenu routes */}
      <Route
        path="/loan-deposit/term-deposit"
        element={
          <PrivateRoute>
            <DepositAccounts />
          </PrivateRoute>
        }
      />

      <Route
        path="/loan-deposits/issue-loan"
        element={
          <PrivateRoute>
            <IssueLoan/>
          </PrivateRoute>
        }
      />

      <Route
        path="/loan-deposit/loan"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Issue Loan"
              description="Process loan applications, disbursements, and loan issuance. Loan issuance system under development."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/loan-deposits/loan-against-deposit"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Loan Against Deposit"
              description="Manage loans secured against deposits, collateral management, and processing. Feature coming soon."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/loan-deposits/renew-term-deposit"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Renew Term Deposit"
              description="Handle term deposit renewals, maturity processing, and reinvestment options. Renewal system under development."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/loan-deposits/loan-top-up"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Loan Top Up"
              description="Process loan top-up requests, additional disbursements, and loan enhancements. Top-up feature coming soon."
            />
          </PrivateRoute>
        }
      />

      {/* Transactions submenu routes */}
      <Route
        path="/transactions/deposit-receipt"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Deposit Receipt"
              description="Generate and manage deposit receipts, transaction records, and deposit confirmations. Receipt system under development."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/transactions/payment"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Payment"
              description="Process payments, loan EMIs, interest payments, and transaction management. Payment system coming soon."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/transactions/expense-voucher"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Expense Voucher"
              description="Manage expense vouchers, operational expenses, and expense tracking. Expense management system under development."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/transactions/journal-voucher"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Journal Voucher"
              description="Handle journal entries, accounting adjustments, and financial transactions. Journal system coming soon."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/transactions/contra"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Contra"
              description="Manage contra entries, cash and bank transfers, and internal transactions. Contra system under development."
            />
          </PrivateRoute>
        }
      />
      <Route
        path="/transactions/transfer-voucher"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Transfer Voucher"
              description="Process transfer vouchers, fund transfers, and inter-account transactions. Transfer system coming soon."
            />
          </PrivateRoute>
        }
      />

      {/* Reports submenu routes */}
      <Route
        path="/reports/member-statement"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Member Statement"
              description="Generate comprehensive member statements, account summaries, and transaction history. Statement generation system under development."
            />
          </PrivateRoute>
        }
      />

      {/* Tools submenu routes */}
      <Route
        path="/tools/db-backup"
        element={
          <PrivateRoute>
            <ComingSoon
              title="DB Backup"
              description="Database backup and restore functionality, data protection, and system maintenance tools. Backup system coming soon."
            />
          </PrivateRoute>
        }
      />

      {/* Settings route */}
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <ComingSoon
              title="Settings"
              description="Configure system settings, user preferences, application parameters, and system administration. Settings panel coming soon."
            />
          </PrivateRoute>
        }
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeContextProvider>
          <ToastProvider>
            <CssBaseline />
            <Router>
              <AxiosInterceptorSetup>
                <AppRoutes />
              </AxiosInterceptorSetup>
            </Router>
          </ToastProvider>
        </ThemeContextProvider>
      </PersistGate>
    </Provider>
  );
}
